import { httpSingleton } from '@/lib/http-client';
import { constructUrl } from '@/lib/utils';
import {
	AI_RESPONSE_SOURCE_IDS,
	AiConversation,
	ReportAiConversationResponse,
	AiConversationSections,
	ConversationQuery,
	FEEDBACK_RATING_ID,
	FollowUpQuestionSuggestion,
	TicketQuery,
} from '@/lib/models';

export interface SendQueryRequestBody {
	query: string;
	aiResponseSourceIds: AI_RESPONSE_SOURCE_IDS[];
	aiProjectId?: string;
	shouldShowSuggestions?: boolean;
	suggestion?: FollowUpQuestionSuggestion;
}

export const aiConversationService = {
	getPastConversations(queryParameters?: { query?: string }) {
		return httpSingleton.orchestrateRequest<{
			aiConversations: AiConversationSections;
			explainerVideoUrl: string;
		}>({
			method: 'GET',
			url: constructUrl('/advisory-service/api/v1/ai-conversations', queryParameters),
		});
	},
	sendQuery(data: SendQueryRequestBody) {
		return httpSingleton.orchestrateRequest<{ query: ConversationQuery }>({
			method: 'POST',
			url: '/advisory-service/api/v1/ai-conversation/query',
			data,
		});
	},
	getConversation(converstaionId: string) {
		return httpSingleton.orchestrateRequest<{ aiConversation: AiConversation }>({
			method: 'GET',
			url: `/advisory-service/api/v1/ai-conversation/${converstaionId}`,
		});
	},
	getReportConversation(aiConversationId: string) {
		return httpSingleton.orchestrateRequest<ReportAiConversationResponse>({
			method: 'GET',
			url: `/advisory-service/api/v1/report/ai-conversation/${aiConversationId}`,
		});
	},
	getDocumentConversation(documentId: string) {
		return httpSingleton.orchestrateRequest<{ aiConversation: AiConversation }>({
			method: 'GET',
			url: `/advisory-service/api/v1/ai-conversation/document/${documentId}`,
		});
	},
	rateConversationQuery(data: {
		conversationQueryId: string;
		feedbackRatingId: FEEDBACK_RATING_ID;
		reason?: string;
	}) {
		return httpSingleton.orchestrateRequest<void>({
			method: 'POST',
			url: '/advisory-service/api/v1/ai-conversation/feedback',
			data,
		});
	},
	deleteConversation(converstaionId: string) {
		return httpSingleton.orchestrateRequest<void>({
			method: 'DELETE',
			url: `/advisory-service/api/v1/ai-conversation/${converstaionId}`,
		});
	},
	showMore(conversationQueryId: string, typeOfData?: string) {
		return httpSingleton.orchestrateRequest<{ query: ConversationQuery }>({
			method: 'POST',
			url: `/advisory-service/api/v1/ai-conversation/query/${conversationQueryId}/show-more`,
			data: { typeOfData: typeOfData },
		});
	},
	updateConversation(aiConversationId: string, data: { title: string }) {
		return httpSingleton.orchestrateRequest<{ aiConversation: AiConversation }>({
			method: 'PUT',
			url: `/advisory-service/api/v1/ai-conversation/${aiConversationId}`,
			data,
		});
	},
	getConversationTicket(data: SendQueryRequestBody) {
		return httpSingleton.orchestrateRequest<{ query: TicketQuery }>({
			method: 'POST',
			url: `/advisory-service/api/v1/ai-conversation/ticket`,
			data,
			withCredentials: true,
		});
	},
	getConverstaionImage(containerId: string, imageName: string) {
		return httpSingleton.orchestrateRequest<{ signedUrl: string }>({
			method: 'GET',
			url: `/advisory-service/api/v1/ai-conversation/image/${containerId}/${imageName}`,
		});
	},
	voiceToText(audio: File) {
		const data = new FormData();
		data.append('audio', audio, audio.name);
		return httpSingleton.orchestrateRequest<{ transcript: string }>({
			method: 'POST',
			url: '/advisory-service/api/v1/ai-conversation/dictate',
			data,
		});
	},
};
